<script lang="ts">
  import { get } from "svelte/store";
  import { debounce } from "$src/application/main/debounce";
  import Input from "$lib/components/ui/input/input.svelte";
  import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";
  import { cn } from "$lib/utils";
  import { useCountries } from "../hooks/useCountries";
  import { type Country } from "$src/domain/map/Country";
  import { getTranslation } from "../../TranslatedTextUtils";
  import { t } from "svelte-i18n";
  
  let container: HTMLElement;
  let listContainer: HTMLElement;

  export let patchHandler: () => void = () => {};
  export let inputHandler: () => void = () => {};
  export let languageCode: string;
  export let value: string = "";

  let displayValue: string = "";

  let isOpen = false;

  let isDirty = false;

  let searchValue: string = "";
  let selectedIndex = -1;


  $: countryQuery = useCountries(languageCode);
  $: filteredCountries = $countryQuery.isSuccess ? filterCountries($countryQuery.data, searchValue) : [];

  $: {
    if (get(countryQuery).isSuccess) {
      displayNameFromCode($countryQuery.data, value);
    }
  }

  function displayNameFromCode(data: Country[] | undefined, countryCode: string) {
    var country = data?.find(country => country.countryCode == value);
    if( country ) {
      displayValue = getTranslation(country.translations, languageCode);
    }
  }


  const dispatch = createEventDispatcher();

  function normalizeString(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}


  function filterCountries(data: Country[] | undefined, query: string) {
    if (data) {

      const normalizedSearchTerm = normalizeString(query);

      return data.filter((country) => {
          const text = normalizeString(getTranslation(country.translations, languageCode));
          return text.startsWith(normalizedSearchTerm)
        }
      );
    }
  }

  function selectCountry(country: Country) {
    value = country.countryCode;
    dispatch("selectCountry", country);
    selectedIndex = -1;
    displayValue = getTranslation(country.translations, languageCode);
    searchValue = value;
    isOpen = false;
    isDirty = false;
    
    patchHandler();
    inputHandler();
  }

  const debouncedFetch = debounce((input: string) => {
    searchValue = displayValue;
  }, 500);

  function handleInput(event: Event) {
    isOpen = true;
    isDirty = true;
    debouncedFetch(searchValue);
  }

  function showOptions(): void {
    searchValue = displayValue;
    isOpen = true;
  }

  function hideOptions(): void {
    isOpen = false;
  }

  function handleClickOutside(event: MouseEvent) {
    if (container && !container.contains(event.target as Node)) {
      isOpen = false;
    }
  }

  async function handleKeydown(event: KeyboardEvent) {
    if (!showOptions) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (filteredCountries) {
        selectedIndex = (selectedIndex + 1) % filteredCountries.length;
        await tick();
        scrollToSelectedOption();
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();

      if (filteredCountries) {
        selectedIndex = (selectedIndex - 1 + filteredCountries.length) % filteredCountries.length;
        await tick();
        scrollToSelectedOption();
      }
    } else if (event.key === "Enter" && selectedIndex >= 0) {
      event.preventDefault();
      if (filteredCountries) {
        selectCountry(filteredCountries[selectedIndex]);
      }
    }
  }

  function scrollToSelectedOption() {
    const selectedElement = listContainer?.children[
      selectedIndex
    ] as HTMLElement;
    if (selectedElement) {
      const elementTop = selectedElement.offsetTop;
      const elementBottom = elementTop + selectedElement.offsetHeight;
      const containerScrollTop = listContainer.scrollTop;
      const containerHeight = listContainer.clientHeight;

      if (elementTop < containerScrollTop) {
        // Scroll up
        listContainer.scrollTop = elementTop;
      } else if (elementBottom > containerScrollTop + containerHeight) {
        // Scroll down
        listContainer.scrollTop = elementBottom - containerHeight;
      }
    }
  }

  function validateInput() {
    if( isDirty && filteredCountries && filteredCountries.length )
    {
      selectCountry(filteredCountries[0]);
    }
  }

  onMount(() => {
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKeydown);
  });

  onDestroy(() => {
    document.removeEventListener("click", handleClickOutside);
    document.removeEventListener("keydown", handleKeydown);
  });
</script>


<div class="relative text-xs" bind:this={container}>
  <Input
    bind:value={displayValue}
    class={cn(isDirty ? 'border-red-500': '', 'capitalize')}
    on:input={handleInput}
    on:click={showOptions}
    on:focusin={showOptions}
    on:focusout={validateInput}
  />
  {#if $countryQuery.isSuccess && isOpen && filteredCountries}

  {#if filteredCountries.length > 0}
  <div class="absolute inset-0 flex items-center pointer-events-none">
    <input
      type="text"
      class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      value={ getTranslation(filteredCountries[0].translations, languageCode)}
      readonly
      style="opacity: 0.7; pointer-events: none; caret-color: transparent;"
    />
  </div>
  {/if}

    <ul
      class="absolute w-full mt-1 border bg-background rounded-lg shadow-lg max-h-44 overflow-auto z-10"
      bind:this={listContainer}
    >
      {#each filteredCountries as country, i}
        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <li
          on:click={() => selectCountry(country)}
          class={cn(
            "px-4 py-2 flex flex-row justify-between items-center cursor-pointer hover:bg-secondary hover:text-secondary-foreground",
            i == selectedIndex ? "bg-secondary text-secondary-foreground" : ""
          )}
        >
          <span class="font-semibold">
            {getTranslation(country.translations, languageCode)}
          </span>
          <span class="text-secondary-foreground/70">
            {country.countryCode}
          </span>
        </li>
      {/each}
    </ul>
  {/if}
</div>